import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const TRANSIENT_PATTERNS = [
  // Netzwerk / Safari
  'Load failed',
  'NetworkError',
  'Network request failed',
  'Failed to fetch',
  'The operation couldn\u2019t be completed',
  "The operation couldn't be completed",
  'cancelled',
  'aborted',
  'AbortError',
  'The user aborted',
  'Suspended while rendering',
  'There was an error while hydrating',
  'Connection closed',
  // Chunk-/Modul-Lade-Fehler (häufig nach Deploy)
  'Importing a module script failed',
  'Failed to fetch dynamically imported module',
  'ChunkLoadError',
  'error loading dynamically imported module',
  'Unable to preload CSS',
];

function isTransientError(err: Error | null | undefined): boolean {
  const msg = err?.message;
  if (!msg) return false;
  return TRANSIENT_PATTERNS.some((p) => msg.includes(p));
}

function isChunkLoadError(err: Error | null | undefined): boolean {
  const msg = err?.message;
  if (!msg) return false;
  return (
    msg.includes('Importing a module script failed') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Unable to preload CSS')
  );
}

/**
 * Error Boundary Component
 * Catches errors in child components and displays a user-friendly error message
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private mountedAt: number = Date.now();

  componentDidMount() {
    this.mountedAt = Date.now();
  }

  componentWillUnmount() {
    if (this.showTimer) clearTimeout(this.showTimer);
  }

  public static getDerivedStateFromError(error: Error): State {
    // Transiente Netzwerk-/Chunk-Fehler dürfen die Fehler-UI gar nicht erst
    // auslösen, sonst flackert beim Seitenwechsel die "Etwas ist schiefgelaufen"-
    // Karte für einen Frame auf, bevor componentDidCatch reagieren kann.
    if (isTransientError(error)) {
      return { hasError: false, error: null };
    }
    // hasError noch nicht setzen – wir verzögern das Rendering in componentDidCatch
    // damit kurze, sich selbst auflösende Fehler (z.B. beim Routenwechsel) kein
    // sichtbares Flackern verursachen.
    return { hasError: false, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Error Boundary caught error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // Auto-reload bei Chunk-Lade-Fehlern (nach Deploys), Cache-Bust.
    // State ist bereits durch getDerivedStateFromError zurückgesetzt,
    // daher kein sichtbares Fehler-UI.
    if (isChunkLoadError(error)) {
      const reloadCountKey = 'moduleErrorReloadCount';
      const lastReloadKey = 'lastModuleErrorReload';
      const now = Date.now();
      const lastReload = parseInt(sessionStorage.getItem(lastReloadKey) || '0', 10);
      const count = parseInt(sessionStorage.getItem(reloadCountKey) || '0', 10);

      // Reset counter if last reload was more than 60s ago
      const recentCount = now - lastReload < 60_000 ? count : 0;

      if (recentCount < 2) {
        sessionStorage.setItem(lastReloadKey, now.toString());
        sessionStorage.setItem(reloadCountKey, String(recentCount + 1));
        const url = new URL(window.location.href);
        url.searchParams.set('_cb', now.toString());
        window.location.replace(url.toString());
      }
      return;
    }

    // Verzögere das Anzeigen der Fehler-UI um ~350ms. Wenn der Subtree
    // innerhalb dieser Zeit neu rendert (z.B. weil React Suspense aufgelöst hat
    // oder ein State-Update gefolgt ist), bleibt nichts sichtbar.
    if (this.showTimer) clearTimeout(this.showTimer);
    this.showTimer = setTimeout(() => {
      this.setState({ hasError: true, error });
    }, 350);
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    // Wenn der Konsument hasError zurücksetzt (Reset/Retry), Timer abbrechen.
    if (prevState.hasError && !this.state.hasError && this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-destructive/10 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <CardTitle>Etwas ist schiefgelaufen</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut.
              </p>
              
              {this.state.error && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Technische Details
                  </summary>
                  <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-auto">
                    {this.state.error.message}
                  </pre>
                </details>
              )}

              <div className="flex gap-2">
                <Button onClick={this.handleReset} variant="default">
                  Erneut versuchen
                </Button>
                <Button 
                  onClick={() => window.location.href = '/'} 
                  variant="outline"
                >
                  Zur Startseite
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
